'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import {
  faXmark,
  faArchive,
  faTrash,
  faEnvelopeOpen,
  faArrowLeft,
  faArrowUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons';
import {
  useGetNotificationByIdQuery,
  useBulkActionMutation,
  useDeleteNotificationMutation,
  useGetOptimizationJobQuery,
} from 'src/api/redux';
import { PulseButton, PulseIconButton } from 'src/components/PulseUI';
import { daysSince } from 'src/utils/daysSince';
import { useAppDispatch } from 'src/redux/hooks';
import { setIsOptimizing } from 'src/redux/slices/territoryManagementSlice';
import { resetOptimization } from 'src/redux/slices/optimizationSlice';

interface NotificationViewProps {
  id: number | string;
  onClose?: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  territory_optimization: 'Territory',
  export: 'Export',
  strategy: 'Strategy',
  crm_sync: 'CRM Sync',
  encounters: 'Encounters',
};

const SEVERITY_STYLES: Record<string, string> = {
  success: 'bg-green-100 text-green-800',
  info: 'bg-blue-100 text-blue-800',
  warning: 'bg-yellow-100 text-yellow-800',
  error: 'bg-red-100 text-red-800',
};

const NotificationView = ({ id, onClose }: NotificationViewProps) => {
  const dispatch = useAppDispatch();
  const markedReadRef = useRef(false);

  const {
    data: notification,
    isLoading,
    error,
  } = useGetNotificationByIdQuery(Number(id), { skip: !id });
  const [bulkAction] = useBulkActionMutation();
  const [deleteNotification] = useDeleteNotificationMutation();

  const isTerritoryOptimization = notification?.category === 'territory_optimization';
  const { data: optimizationJob } = useGetOptimizationJobQuery(
    notification?.source_object_id ?? 0,
    { skip: !isTerritoryOptimization || !notification?.source_object_id },
  );

  useEffect(() => {
    if (notification && !notification.read_at && !markedReadRef.current) {
      markedReadRef.current = true;
      bulkAction({ action: 'mark-read', ids: [Number(notification.id)] });
    }
  }, [notification, bulkAction]);

  const handleArchive = async () => {
    await bulkAction({ action: 'archive', ids: [Number(id)] });
    onClose?.();
  };

  const handleDelete = async () => {
    await deleteNotification(Number(id));
    onClose?.();
  };

  if (isLoading) {
    return (
      <div
        data-testid="notification-view-loading"
        className="flex flex-1 flex-col gap-4 rounded-xl bg-white p-6"
      >
        <div className="h-6 w-32 animate-pulse rounded-lg bg-neutral-100" />
        <div className="h-8 w-64 animate-pulse rounded-lg bg-neutral-100" />
        <div className="h-4 w-full animate-pulse rounded-lg bg-neutral-100" />
        <div className="h-4 w-3/4 animate-pulse rounded-lg bg-neutral-100" />
      </div>
    );
  }

  if (error || !notification) {
    return (
      <div
        data-testid="notification-view-error"
        className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl bg-white p-6"
      >
        <p className="text-sm text-neutral-500">Notification not found</p>
        <PulseButton
          size="sm"
          variant="plain"
          startDecorator={faArrowLeft}
          onClick={onClose}
          data-testid="notification-view-back-error"
          aria-label="Back to notifications"
        >
          Back to Notifications
        </PulseButton>
      </div>
    );
  }

  const categoryLabel =
    CATEGORY_LABELS[notification.category] || notification.category;
  const severityClass =
    SEVERITY_STYLES[notification.severity] || SEVERITY_STYLES.info;

  const optimizationLink = isTerritoryOptimization && optimizationJob
    ? `/territory-manager/results/${notification.source_object_id}`
    : null;

  return (
    <div
      data-testid="notification-view"
      className="flex flex-1 flex-col overflow-hidden rounded-xl bg-white"
    >
      {/* Header with title and actions */}
      <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3">
        <h2 className="text-base font-semibold text-neutral-900">
          Notification
        </h2>
        <div role="toolbar" aria-label="Notification actions" className="flex items-center gap-2">
          {!notification.read_at && (
            <PulseButton
              size="sm"
              variant="soft"
              startDecorator={faEnvelopeOpen}
              onClick={() =>
                bulkAction({
                  action: 'mark-read',
                  ids: [Number(notification.id)],
                })
              }
              aria-label="Mark as read"
              data-testid="notification-view-mark-read"
            >
              Mark as read
            </PulseButton>
          )}
          <PulseButton
            size="sm"
            variant="soft"
            color="neutral"
            startDecorator={faArchive}
            onClick={handleArchive}
            aria-label="Archive notification"
            data-testid="notification-view-archive"
          >
            Archive
          </PulseButton>
          <PulseButton
            size="sm"
            variant="soft"
            color="danger"
            startDecorator={faTrash}
            onClick={handleDelete}
            aria-label="Delete notification"
            data-testid="notification-view-delete"
          >
            Delete
          </PulseButton>
          <PulseIconButton
            icon={faXmark}
            variant="plain"
            color="neutral"
            size="sm"
            onClick={onClose}
            aria-label="Close notification"
            data-testid="notification-view-back"
          />
        </div>
      </div>

      {/* Content */}
      <div role="article" aria-label={notification.title} className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
        {/* Meta row */}
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${severityClass}`}
            data-testid="notification-view-severity"
          >
            {categoryLabel}
          </span>
          <span className="text-xs text-neutral-400">
            {daysSince(notification.created_at)}
          </span>
        </div>

        <h1
          className="text-lg font-semibold text-neutral-900"
          data-testid="notification-view-title"
        >
          {notification.title}
        </h1>

        {notification.body && (
          <p
            className="text-sm leading-relaxed text-neutral-600"
            data-testid="notification-view-body"
          >
            {notification.body}
          </p>
        )}

        {optimizationLink && (
          <Link
            href={optimizationLink}
            data-testid="notification-view-optimization-link"
            className="self-start"
            onClick={() => {
              dispatch(resetOptimization());
              dispatch(setIsOptimizing(false));
            }}
          >
            <PulseButton
              size="sm"
              variant="soft"
              endDecorator={faArrowUpRightFromSquare}
              aria-label="View territory optimization"
            >
              View Optimization
            </PulseButton>
          </Link>
        )}
      </div>
    </div>
  );
};

export default NotificationView;
